class B2BOffer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
  }

  connectedCallback() {
    const variantsScript = this.querySelector("[data-dynamic-variants]");
    try {
      this.productVariants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    } catch (e) {
      this.productVariants = [];
    }
    this.cart = document.querySelector("cart-drawer");

    this.querySelectorAll('[id^="B2BProductForm-"]').forEach(form => {
      form.addEventListener("submit", (e) => {
        this.onFormSubmit(e, form);
      })
    });

    this.querySelector('[id^="B2BProductOptions-"]')?.addEventListener("click", (e) => {
      const card = e.target.closest('.b2b-offer__pack-card');
      if (!card) return;
      const input = card.querySelector('input[type="radio"]');
      if (!input) return;
      input.checked = true;

      this.querySelectorAll(".b2b-offer__subscription-form").forEach(form => {
        form.classList.toggle("active", form.dataset.variant == input.dataset.variant);
      });
    });

    // Run each initialiser independently so one failure can't block the others
    // (e.g. a missing gallery must not stop the DIG offer from wiring up).
    [
      this.initRadioCards,
      this.initGallery,
      this.initClickListeners,
      this.initDigOffer,
    ].forEach(fn => {
      try {
        fn.call(this);
      } catch (e) {
        console.error("B2BOffer init error:", e);
      }
    });
  }

  /* ============================================
     DIG Group Offer — simplified specials (radio selectors)
     Pick a package + size; the summary, profit projection and Add to
     cart reflect the selected variant. Submitting adds to cart and
     opens the drawer.
     ============================================ */
  initDigOffer() {
    // A section can contain more than one DIG offer block (e.g. the full
    // group offer and the 14-Day-only block) — wire each root independently.
    this.querySelectorAll("[data-dig-offer]").forEach(root => this.initDigOfferRoot(root));
  }

  initDigOfferRoot(root) {
    root.addEventListener("change", (e) => {
      const input = e.target;
      if (!input || input.tagName !== "INPUT") return;

      if (input.matches("[data-dig-package-input]")) {
        this.selectDigPackage(root, input.value);
        return;
      }

      const form = input.closest("[data-dig-form]");
      if (form && input.name.indexOf("dig-tier-") === 0) {
        this.updateDigForm(form);
      }
    });

    // Clicking a card label forces selection even if native label->input misfires.
    root.addEventListener("click", (e) => {
      const label = e.target.closest("label.b2b-offer__pack-card");
      if (!label) return;
      const input = label.querySelector('input[type="radio"]');
      if (!input || input.disabled || input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });

    root.querySelectorAll("[data-dig-form]").forEach(form => {
      form.addEventListener("submit", (e) => this.onDigAddToCart(e, form));
      this.updateDigForm(form);
    });

    // Apply the gallery filter for the package selected on load.
    let initialHandle = null;
    const checkedPkg = root.querySelector("[data-dig-package-input]:checked");
    if (checkedPkg) {
      initialHandle = checkedPkg.value;
    } else {
      const activePanel =
        root.querySelector("[data-dig-package].active") || root.querySelector("[data-dig-package]");
      if (activePanel) initialHandle = activePanel.dataset.digPackage;
    }
    this.syncGalleryToPackage(initialHandle, { initial: true });
  }

  selectDigPackage(root, handle) {
    root.querySelectorAll("[data-dig-package]").forEach(panel => {
      const isActive = panel.dataset.digPackage === handle;
      panel.classList.toggle("active", isActive);
      if (isActive) {
        const form = panel.querySelector("[data-dig-form]");
        if (form) this.updateDigForm(form);
      }
    });

    // Keep the media gallery in sync with the chosen package.
    this.syncGalleryToPackage(handle);
  }

  getDigSelectedTier(form) {
    return (
      form.querySelector('[name^="dig-tier-"]:checked') ||
      form.querySelector('[name^="dig-tier-"]:not(:disabled)')
    );
  }

  updateDigForm(form) {
    const tier = this.getDigSelectedTier(form);
    if (!tier) return;

    const price = parseInt(tier.dataset.price) || 0;
    const units = parseInt(tier.dataset.units) || 1;
    const cases = parseInt(tier.dataset.cases) || 0;
    const retail = parseInt(tier.dataset.retail) || 0;
    const profitPer = parseInt(tier.dataset.profitPer) || 0; // cents of profit per unit

    const days = parseInt(form.dataset.days) || 20;
    const scenarios = (form.dataset.scenarios || "3,5,10")
      .split(",")
      .map(n => parseInt(n.trim()))
      .filter(n => !isNaN(n) && n > 0);

    // Hidden field used on add to cart
    form.querySelector('[name="id"]').value = tier.dataset.variant;

    // Summary label + total + CTA amount
    const caseWord = cases === 1 ? "Case" : "Cases";
    this.setText(form, "[data-dig-summary-label]", cases + " " + caseWord + " (" + units + " Units)");
    this.setText(form, "[data-dig-billed]", this.formatMoney(price));
    this.setText(form, "[data-dig-cta-amount]", this.formatMoney(price));

    // Shipping is shown statically per option in the section Liquid (charged at checkout) — no JS needed.

    // Profit projection
    const revenue = units * profitPer;
    this.setText(form, "[data-dig-summary-revenue]",
      "Est. Profit @ " + this.formatMoney(retail) + " Retail: " + this.formatMoney(revenue));
    this.setText(form, "[data-dig-profit-per]",
      "Earn " + this.formatMoney(profitPer) + " Pure Profit Per Unit");

    const rowsEl = form.querySelector("[data-dig-projection-rows]");
    if (rowsEl) {
      rowsEl.innerHTML = scenarios
        .map(perDay => {
          const monthly = perDay * profitPer * days; // cents
          const yearly = monthly * 12;
          return (
            '<div class="dig-offer__projection-row">' +
            '<span class="dig-offer__projection-scenario">Sell ' + perDay + "/day</span>" +
            '<span class="dig-offer__projection-mo">' + this.formatMoney(monthly) + " / mo</span>" +
            '<span class="dig-offer__projection-yr">' + this.formatMoney(yearly) + " / yr</span>" +
            "</div>"
          );
        })
        .join("");
    }
  }

  setText(form, selector, text) {
    const el = form.querySelector(selector);
    if (el) el.textContent = text;
  }

  onDigAddToCart(event, form) {
    if (event) event.preventDefault();
    if (this.isLoading) return;

    const idInput = form.querySelector('[name="id"]');
    const variantId = idInput ? parseInt(idInput.value) : 0;
    if (!variantId) return;

    const qtyInput = form.querySelector('[name="quantity"]');
    const cartData = {
      items: [
        {
          id: variantId,
          quantity: qtyInput ? parseInt(qtyInput.value) || 1 : 1,
        },
      ],
    };

    if (this.cart) {
      cartData["sections"] = this.cart.getSectionsToRender().map(section => section.id);
      cartData["sections_url"] = window.location.pathname;
    }

    this.addToCart(cartData);
  }

  initRadioCards() {
    this.querySelectorAll('.b2b-offer__subscription-form').forEach(form => {
      form.querySelectorAll('.b2b-offer__radio-card input[name="selling_plan"]').forEach(input => {
        input.addEventListener('change', () => {
          this.updateSummary(form);
        });
      });
    });
  }

  updateSummary(form) {
    const checkedInput = form.querySelector('[name="selling_plan"]:checked');
    if (!checkedInput) return;

    const total = checkedInput.dataset.total;
    const formattedTotal = this.formatMoney(parseInt(total));

    const summaryTotal = form.querySelector('[data-summary-total]');
    const ctaPrice = form.querySelector('[data-cta-price]');

    if (summaryTotal) summaryTotal.textContent = formattedTotal;
    if (ctaPrice) ctaPrice.textContent = formattedTotal;

    const isSubscribe = checkedInput.value !== '';
    const units = parseInt(form.dataset.units);
    const unitCost = parseInt(isSubscribe ? form.dataset.subUnitCost : form.dataset.onetimeUnitCost);
    const profit = units * (12000 - unitCost);
    const revenueEl = form.querySelector('[data-summary-revenue]');
    if (revenueEl) revenueEl.textContent = 'Est. Profit @ $120 Retail: ' + this.formatMoney(profit);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  onFormSubmit(event = false, form) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    const sellingPlanInput = form.querySelector("[name='selling_plan']:checked");
    const cartData = {
      items: [
        {
          id: parseInt(form.querySelector("[name='id']").value),
          quantity: parseInt(form.querySelector("[name='quantity']").value),
        }
      ]
    };

    if (sellingPlanInput && sellingPlanInput.value) {
      cartData.items[0].selling_plan = parseInt(sellingPlanInput.value);
    }

    if (this.cart) {
      cartData['sections'] = this.cart.getSectionsToRender().map((section) => section.id)
      cartData['sections_url'] = window.location.pathname
    }

    this.addToCart(cartData);
  }

  addToCart(data) {
    if (!data) return;

    this.toggleLoading();

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading();

        if (this.cart) {
          this.cart.renderContents(response);
          this.cart.classList.remove("is-empty");
          this.cart.open();
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading(true));
  }

  initGallery() {
    this.gallery = this.querySelector('[id^="B2BProductGallery-"]');
    this.navGallery = this.querySelector('[id^="B2BProductGalleryNav-"]');

    this.gallerySlide = 1;

    if (!this.gallery) return;

    // packageSync: move the main image to the chosen package (DIG offer present).
    // packageFilter: additionally hide the other packages' images (toggle on).
    const galleryWrapper = this.querySelector("[data-gallery]");
    this.gallerySyncEnabled = galleryWrapper?.dataset.packageSync === "true";
    this.galleryFilterEnabled = galleryWrapper?.dataset.packageFilter === "true";

    this.gallery.addEventListener('scroll', () => {
      const items = this.visibleMainItems();
      if (!items.length) return;

      const scrollLeft = this.gallery.scrollLeft;
      const blockWidth = items[0]?.offsetWidth ?? 0;
      const gap = 12;

      // Index among *visible* items (hidden items collapse to zero width).
      const index = Math.round(scrollLeft / (blockWidth + gap));
      this.gallerySlide = index + 1;

      items.forEach((item, i) => {
        const video = item.querySelector("video");
        if (!video) return;
        if (i === index) video.play(); else video.pause();
      });

      this.syncActiveNav(index);
    });
  }

  // Items currently shown (the filter hides the rest via display:none).
  visibleMainItems() {
    if (!this.gallery) return [];
    return Array.from(this.gallery.querySelectorAll("li")).filter(li => li.style.display !== "none");
  }

  visibleNavItems() {
    if (!this.navGallery) return [];
    return Array.from(this.navGallery.querySelectorAll("li")).filter(li => li.style.display !== "none");
  }

  // Highlight the nav thumbnail for the given visible index.
  syncActiveNav(index = this.gallerySlide - 1) {
    if (this.navGallery) {
      this.navGallery.querySelectorAll("li").forEach(li => li.classList.remove("active"));
    }
    const nav = this.visibleNavItems();
    if (nav[index]) nav[index].classList.add("active");
  }

  initClickListeners() {
    this.addEventListener("click", (e) => {
      const actionEl = e.target.closest("[data-action]");
      if (!actionEl) return;

      switch (actionEl.dataset.action) {
        case "prev-slide":
          this.goToVisibleSlide(this.gallerySlide - 2);
          break;
        case "next-slide":
          this.goToVisibleSlide(this.gallerySlide);
          break;
        case "change-slide": {
          const li = actionEl.closest("li");
          const index = this.visibleNavItems().indexOf(li);
          if (index >= 0) this.goToVisibleSlide(index);
          break;
        }
      }
    });
  }

  // Scroll the main gallery to the visible item at `index` (0-based, clamped).
  goToVisibleSlide(index, behavior = 'smooth') {
    const items = this.visibleMainItems();
    if (!items.length) return;

    const clamped = Math.max(0, Math.min(index, items.length - 1));
    const target = items[clamped];
    if (!target) return;

    this.gallery.scrollTo({ left: target.offsetLeft, behavior });
    this.gallerySlide = clamped + 1;
    this.syncActiveNav(clamped);
  }

  // Show only the media grouped under `handle` (variant.option1 handle).
  // Falls back to showing everything when the package has no grouped media.
  applyGalleryPackageFilter(handle) {
    if (!this.galleryFilterEnabled || !this.gallery) return;

    const mainItems = Array.from(this.gallery.querySelectorAll("li"));
    const navItems = this.navGallery ? Array.from(this.navGallery.querySelectorAll("li")) : [];
    const matches = (li) => (li.dataset.package || "") === handle;
    const hasMatch = mainItems.some(matches);

    const setVisible = (li, visible) => { li.style.display = visible ? "" : "none"; };
    mainItems.forEach(li => setVisible(li, hasMatch ? matches(li) : true));
    navItems.forEach(li => setVisible(li, hasMatch ? matches(li) : true));

    // Reset to the first visible slide and pause any off-screen videos.
    this.gallery.scrollLeft = 0;
    this.gallerySlide = 1;
    this.visibleMainItems().forEach((item, i) => {
      if (i !== 0) item.querySelector("video")?.pause();
    });
    this.syncActiveNav(0);
  }

  // Move the main image to a package's first (anchor) image without hiding
  // anything — used when the filter toggle is off so the hero still tracks
  // the selected package.
  scrollToPackage(handle, behavior = 'smooth') {
    if (!this.gallery) return;
    const items = this.visibleMainItems();
    const index = items.findIndex(li => (li.dataset.package || "") === handle);
    if (index >= 0) this.goToVisibleSlide(index, behavior);
  }

  // Single entry point on package change / load. Filters when the toggle is on,
  // otherwise just moves the main image to the selected package's image.
  syncGalleryToPackage(handle, opts = {}) {
    if (!this.gallery || handle == null) return;
    if (this.galleryFilterEnabled) {
      this.applyGalleryPackageFilter(handle);
    } else if (this.gallerySyncEnabled && !opts.initial) {
      this.scrollToPackage(handle);
    }
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("b2b-offer", B2BOffer);